import {Injectable} from "@angular/core";
import {delay} from "rxjs/operators";
import {HandlerRegistry} from "../common/handlers/handler-registry.service";
import {QueryGateway} from "../common/handlers/query-gateway";
import {FacetResult, FacetValueResult, GetFacets} from "@portbase/bezoekschip-service-typescriptmodels";
import {Observable, of} from "rxjs";
import {BezoekschipQueryHandlerMock} from "./bezoekschip-query-handler.mock";
import {CommercialReleaseMock} from "./commercial-release.mock";

/**
 * Local mock for the faceted-overview facets query (fired on load and on every quick-view switch).
 *
 * The overviews dispatch GetFacets through the HANDLERS gateway ({@link QueryGateway} in
 * common/handlers, via `sendFacetsQuery`) — a DIFFERENT gateway than the legacy one that the
 * {@link QueryHandler} base class (and {@link BezoekschipQueryHandlerMock}) register on. Registering on
 * the legacy gateway (as this mock previously did) leaves GetFacets unhandled locally, so it falls
 * through to the real `/api/query` and 404s in local mock mode.
 *
 * So we register on the handlers gateway, but still honour the mock discipline (see
 * rules/services-state.md): registration happens in the INSTANCE CONSTRUCTOR (never at class-load — no
 * `@HandleQuery` decorator), gated to local/mock only because AppModule instantiates this mock via
 * `injector.get(FacetsQueryHandlerMock)` behind the `!environment.production && !environment.docker`
 * guard. The invoker is registered WITHOUT `{global:true}` (the instance carries no elementRef, so it is
 * already treated as a global handler) and BEFORE the instance so the gateway can wire it up on
 * registration. GetFacets has no real LOCAL handler, so there is no duplicate FQN.
 */
@Injectable()
export class FacetsQueryHandlerMock {
  private static readonly GET_FACETS = "com.portbase.bezoekschip.common.api.common.facets.GetFacets";

  // `queryGateway` and `handlerRegistry` are taken as real constructor dependencies (rather than
  // resolved via the static `InjectorProvider.injector.get(...)` escape hatch) so Angular's DI
  // system is FORCED to construct the handlers-gateway `QueryGateway` instance -- and therefore run
  // its `Gateway` base-class constructor, which subscribes to `HandlerRegistry.onRegistered` --
  // BEFORE this constructor's body runs. That closes off any theoretical ordering edge case (e.g. a
  // future refactor that bounds the `ReplaySubject`'s replay buffer), and matches how every other
  // handler in this codebase (via `@Handler()`/`extends View`, and the legacy `QueryHandler` base
  // class) lets Angular's DI manage the dependency instead of a static injector lookup.
  constructor(
    private bezoekschipMock: BezoekschipQueryHandlerMock,
    private handlerRegistry: HandlerRegistry
  ) {
    QueryGateway.registerHandlerInvoker(FacetsQueryHandlerMock.name, FacetsQueryHandlerMock.GET_FACETS, this.getFacets);
    this.handlerRegistry.registerHandlerInstance(FacetsQueryHandlerMock.name, this);
  }

  getFacets = (query: GetFacets): Observable<FacetResult> => of(this.buildFacets(query)).pipe(delay(100));

  // Returns facet stats derived from the SAME mock dataset the wrapped list query serves, keyed on the
  // inner query class set by `sendFacetsQuery`. This keeps the sidebar counts (and the visit overview's
  // total count, taken from the "count" facet) consistent with the rows that actually render.
  private buildFacets(getFacets: GetFacets): FacetResult {
    const innerClass = (getFacets?.query as { "@class"?: string })?.["@class"];
    if (innerClass?.endsWith("visit.FindVisits")) {
      return this.visitFacets();
    }
    if (innerClass?.endsWith("consignments.queries.FindConsignments")) {
      return this.consignmentFacets();
    }
    if (innerClass?.endsWith("containers.queries.FindContainers")) {
      return this.containerFacets();
    }
    if (innerClass?.endsWith("commercialrelease.FindCommercialReleaseSummaries")) {
      return this.commercialReleaseFacets();
    }
    return {facets: {}};
  }

  private visitFacets(): FacetResult {
    const summaries = this.bezoekschipMock.getVisitSummaries();
    return {
      facets: {
        count: [{value: "count", count: summaries.length}],
        visitStatus: this.facetValues(summaries, s => s.visitStatus)
      }
    };
  }

  private consignmentFacets(): FacetResult {
    const dataset = BezoekschipQueryHandlerMock.getConsignmentDataset();
    return {
      facets: {
        count: [{value: "count", count: dataset.length}],
        ensStatus: this.facetValues(dataset, c => c.ensStatus),
        temporaryStorageStatus: this.facetValues(dataset, c => c.temporaryStorageStatus)
      }
    };
  }

  private containerFacets(): FacetResult {
    const dataset = BezoekschipQueryHandlerMock.getContainerDataset();
    return {
      facets: {
        count: [{value: "count", count: dataset.length}],
        status: this.facetValues(dataset, c => c.status)
      }
    };
  }

  // Mirrors the real `FindCommercialReleaseSummaries` GetFacets shape (confirmed against a captured
  // real response): `count` (total), `declarationStatus` and `containerOperatorShortName` (the two
  // user-facing sidebar filters wired in `commercial-release-overview-new.component.ts`), plus
  // `declarantShortName`/`allowedViewers`/`crn` (all hidden client-side, but still returned here so the
  // local mock backend matches production shape). The mock dataset has no dedicated
  // `containerOperatorShortName`/`allowedViewers` fields, so those are derived from the closest existing
  // mock field (`releaseData.containerOperator.cargoDeclarantShortName`/`declarantShortName`) to keep the
  // sidebar filters exercisable locally without inventing new mock data shapes.
  private commercialReleaseFacets(): FacetResult {
    const dataset = CommercialReleaseMock;
    return {
      facets: {
        count: [{value: "1", count: dataset.length}],
        declarationStatus: this.facetValues(dataset, d => (d.releaseStatus as { status?: string })?.status),
        containerOperatorShortName: this.facetValues(dataset, d => d.releaseData?.containerOperator?.cargoDeclarantShortName),
        "releaseData/terminalShortName": [
          {value: "ECTDELTA", count: 14},
          {value: "EUROMAX", count: 9},
          {value: "APMII", count: 6},
          {value: "APMRTM", count: 4},
          {value: "RWG", count: 3},
          {value: "RST", count: 2}
        ],
        declarantShortName: this.facetValues(dataset, d => d.declarantShortName),
        allowedViewers: this.facetValues(dataset, d => d.declarantShortName),
        crn: this.facetValues(dataset, d => d.crn)
      }
    };
  }

  private facetValues<T>(items: T[], accessor: (item: T) => string | undefined | null): FacetValueResult[] {
    const counts: { [value: string]: number } = {};
    items.forEach(item => {
      const value = accessor(item);
      if (value != null) {
        counts[value] = (counts[value] || 0) + 1;
      }
    });
    return Object.entries(counts).map(([value, count]) => (<FacetValueResult>{value, count}));
  }
}
